import React, { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import { getPayrolls } from 'services/payrolls';
import { cn } from 'utils';
import styles from './dashboard.module.scss';
import Select from 'components/Select';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const fmtMoney = (n) =>
    n == null ? '$0.00' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getLast12Months = () => {
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
            label: d.toLocaleString('default', { month: 'short' }),
            key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        });
    }
    return months;
};

const getMonthKey = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const DashboardCharts = () => {
    const { data: payrollsRaw = [] } = useQuery({
        queryKey: ['payrolls'],
        queryFn: getPayrolls,
        staleTime: 5 * 60 * 1000,
    });

    const approvedPayrolls = useMemo(() => {
        const list = Array.isArray(payrollsRaw) ? payrollsRaw : [];
        return list.filter(p => p.status === 'approved');
    }, [payrollsRaw]);

    return (
        <div className='container-fluid padding-0'>
            <div className={cn('row g-2', styles.chartsContainer)}>
                <div className={cn('col-lg-8 col-12')}>
                    <BarChartCard payrolls={approvedPayrolls} />
                </div>
                <div className={cn('col-lg-4 col-12')}>
                    <DoughnutChartCard payrolls={approvedPayrolls} />
                </div>
            </div>
        </div>
    );
};

const BarChartCard = ({ payrolls }) => {
    const months = useMemo(() => getLast12Months(), []);

    const { grossByMonth, taxesByMonth } = useMemo(() => {
        const gross = {};
        const taxes = {};
        months.forEach(m => { gross[m.key] = 0; taxes[m.key] = 0; });
        payrolls.forEach(p => {
            const key = getMonthKey(p.payDate || p.endDate || p.startDate);
            if (key && gross[key] !== undefined) {
                gross[key] += Number(p.grossPay || 0);
                taxes[key] += Number(p.totalTaxes || 0);
            }
        });
        return { grossByMonth: gross, taxesByMonth: taxes };
    }, [payrolls, months]);

    const currentYear = new Date().getFullYear();

    const chartData = {
        labels: months.map(m => m.label),
        datasets: [
            {
                label: 'Taxes',
                data: months.map(m => taxesByMonth[m.key]),
                backgroundColor: '#FFC7A5',
                stack: 'Stack 0',
                pointStyle: 'circle',
            },
            {
                label: 'Gross Pay',
                data: months.map(m => grossByMonth[m.key]),
                backgroundColor: '#E7730A',
                stack: 'Stack 0',
                pointStyle: 'circle',
            },
        ],
    };

    const chartOptions = {
        plugins: {
            title: { display: false },
            legend: { display: false },
            tooltip: {
                enabled: true,
                backgroundColor: '#FFFFFF',
                titleColor: '#1D2630',
                bodyColor: '#1D2630',
                usePointStyle: true,
                callbacks: {
                    label: (item) => `  ${item.dataset.label}: ${fmtMoney(item.raw)}`,
                    title: (items) => `${items?.[0]?.label} ${currentYear}`,
                },
            },
        },
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        scales: {
            x: { stacked: true },
            y: {
                stacked: true,
                ticks: {
                    callback: (val) => `$${Number(val).toLocaleString('en-US')}`,
                },
            },
        },
    };

    return (
        <div className={styles.chartCard}>
            <div className={styles.header}>
                <p className={styles.title}>Payroll Cost Overview</p>
                <Select
                    value='Liabilities (YTD)'
                    options={[{ label: 'Liabilities (YTD)', value: 'Liabilities (YTD)' }]}
                    color='white'
                />
            </div>
            <Bar options={chartOptions} data={chartData} />
            <div className={styles.barChartFooter}>
                <div className={styles.legend}>
                    <div style={{ backgroundColor: '#E7730A' }} className={styles.color} />
                    <p>Gross Pay</p>
                </div>
                <div className={styles.legend}>
                    <div style={{ backgroundColor: '#FFC7A5' }} className={styles.color} />
                    <p>Taxes</p>
                </div>
            </div>
        </div>
    );
};

const DoughnutChartCard = ({ payrolls }) => {
    const currentYear = new Date().getFullYear();

    const { ytdGross, ytdTaxes, ytdNet, ytdDeductions, ytdCount } = useMemo(() => {
        let gross = 0, taxes = 0, net = 0, count = 0;
        payrolls.forEach(p => {
            const dateStr = p.payDate || p.endDate || p.startDate;
            if (!dateStr) return;
            if (new Date(dateStr).getFullYear() === currentYear) {
                gross += Number(p.grossPay || 0);
                taxes += Number(p.totalTaxes || 0);
                net += Number(p.netPay || 0);
                count++;
            }
        });
        const deductions = Math.max(0, gross - net - taxes);
        return { ytdGross: gross, ytdTaxes: taxes, ytdNet: net, ytdDeductions: deductions, ytdCount: count };
    }, [payrolls, currentYear]);

    const hasData = ytdGross > 0;

    const doughnutData = {
        datasets: [{
            label: 'Payroll Summary',
            data: hasData
                ? [ytdNet, ytdTaxes, ytdDeductions || 0.01]
                : [1, 1, 1],
            backgroundColor: ['#0067A7', '#983A00', '#E58A00'],
            pointStyle: 'circle',
            borderWidth: hasData ? 1 : 0,
        }],
    };

    const doughnutOptions = {
        plugins: {
            tooltip: {
                usePointStyle: true,
                callbacks: {
                    label: (item) => {
                        const labels = ['Net Pay', 'Taxes', 'Deductions'];
                        return `  ${labels[item.dataIndex]}: ${fmtMoney(hasData ? item.raw : 0)}`;
                    },
                },
            },
            legend: { display: false },
        },
    };

    const periodLabel = `Jan 1 \u2013 Today, ${currentYear}`;

    const summary = [
        { label: 'Gross Pay (YTD)',  value: fmtMoney(ytdGross),  color: '#DC2626' },
        { label: 'Net Pay (YTD)',    value: fmtMoney(ytdNet),    color: '#0067A7' },
        { label: 'Taxes (YTD)',      value: fmtMoney(ytdTaxes),  color: '#983A00' },
        { label: `Payrolls (${currentYear})`, value: ytdCount,   color: '#E58A00' },
    ];

    return (
        <div className={styles.chartCard}>
            <div className={styles.header}>
                <p className={styles.title}>Payroll Summary</p>
                <p className={styles.secondaryTitle}>View report</p>
            </div>
            <div className={styles.doughnutCharConainer}>
                <Doughnut options={doughnutOptions} data={doughnutData} />
            </div>
            <div className={styles.doughnutFooter}>
                <Select
                    color='white'
                    value='ytd'
                    options={[{ label: periodLabel, value: 'ytd' }]}
                />
                <div className={cn('container-fluid padding-0')}>
                    <div className={cn('row g-3', styles.summaryDetails)}>
                        {summary.map(({ label, value, color }, index) => (
                            <div key={index} className={cn('col-6')}>
                                <div className={styles.card}>
                                    <p className={styles.label}><span style={{ backgroundColor: color }} />{label}</p>
                                    <p className={styles.value}>{value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardCharts;
